import React, { useId, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { clinicalAmendmentService, type ClinicalAmendment } from '../../../services/clinicalAmendmentService';
import type { MedicalConsultation } from '../../../types/api';
import { canSignAmendment } from '../hooks/correctionAccess';
import { correctionErrorMessage } from '../hooks/correctionErrors';
import AmendmentList from './AmendmentList';

interface AmendmentDrawerProps {
  show: boolean;
  consultation: MedicalConsultation;
  amendments: ClinicalAmendment[];
  onClose: () => void;
  onChanged: () => void;
}

const EMPTY_FORM = { reason: '', amendment_text: '' };

/**
 * Side panel to correct a signed consultation with an addendum
 * (clinical-record-amendments R1, R4). The original is never edited: every
 * amendment is saved as a draft, and signing is a separate explicit call even
 * when offered as a single "Guardar y firmar" action. Rendered only for users
 * who pass `canAmendConsultation`; the backend re-checks every call.
 */
const AmendmentDrawer: React.FC<AmendmentDrawerProps> = ({ show, consultation, amendments, onClose, onChanged }) => {
  const { user } = useAuth();
  const titleId = useId();
  const [form, setForm] = useState(EMPTY_FORM);
  /**
   * The draft being edited, with the version the edit is based on (409 on
   * mismatch). `conflicted` marks a draft someone else saved meanwhile: the
   * doctor's text is kept, and the next save knowingly targets the version
   * now shown in the list.
   */
  const [editing, setEditing] = useState<{ id: number; lockVersion: number; conflicted?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!show) return null;

  const userCanSign = canSignAmendment({ status: 'draft', doctor_id: consultation.doctor_id } as ClinicalAmendment, user);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
  };

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (err) {
      setError(correctionErrorMessage(err, 'la enmienda'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save, then (optionally) sign as a second call (R4). Once the draft exists
   * the drawer switches to editing THAT draft before attempting the sign, so
   * a failed sign never leaves an orphan behind and a retry goes through
   * update + sign on the same row instead of creating another one.
   */
  const save = async (andSign: boolean) => {
    if (!form.reason.trim() || !form.amendment_text.trim()) {
      setError('El motivo y el texto de la enmienda son obligatorios.');
      return;
    }
    setBusy(true);
    setError(null);
    let saved: ClinicalAmendment;
    try {
      if (editing) {
        const lockVersion = editing.conflicted
          ? (amendments.find((a) => a.id === editing.id)?.lock_version ?? editing.lockVersion)
          : editing.lockVersion;
        saved = await clinicalAmendmentService.update(editing.id, { ...form, lock_version: lockVersion });
      } else {
        saved = await clinicalAmendmentService.create(consultation.id, form);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409 && editing) {
        // Someone else saved this draft first. Never discard the doctor's
        // typed clinical text: keep the form, reload the list so the other
        // version is visible, and let the doctor decide.
        setEditing({ ...editing, conflicted: true });
        setError('Otro usuario modificó este borrador. Sus cambios sin guardar se conservan en el formulario: revise la versión actualizada en la lista y guarde de nuevo para reemplazarla, o cancele la edición.');
        onChanged();
      } else {
        setError(correctionErrorMessage(err, 'la enmienda'));
      }
      setBusy(false);
      return;
    }

    setEditing(saved.lock_version !== undefined ? { id: saved.id, lockVersion: saved.lock_version } : null);
    onChanged();

    if (andSign) {
      try {
        if (saved.lock_version === undefined) {
          throw new Error('No se recibió la versión del borrador. Recargue la página.');
        }
        await clinicalAmendmentService.sign(saved.id, saved.lock_version);
        onChanged();
      } catch (err) {
        // Server rejections (axios errors) carry the backend's own Spanish
        // reason; only the local missing-version precondition uses message.
        const reason = !axios.isAxiosError(err) && err instanceof Error ? err.message : correctionErrorMessage(err, 'la enmienda');
        setError(`Borrador guardado, pero no se pudo firmar: ${reason}`);
        setBusy(false);
        return;
      }
    }
    resetForm();
    setBusy(false);
  };

  const startEdit = (a: ClinicalAmendment) => {
    setEditing({ id: a.id, lockVersion: a.lock_version });
    setForm({ reason: a.reason, amendment_text: a.amendment_text });
  };

  return (
    <>
      <div className="offcanvas-backdrop fade show" onClick={busy ? undefined : onClose} />
      <div className="offcanvas offcanvas-end show" style={{ visibility: 'visible', width: 520 }} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title" id={titleId}><i className="fa fa-file-signature me-2"></i>Enmiendas de la consulta</h5>
          <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} disabled={busy} />
        </div>
        <div className="offcanvas-body">
          <p className="text-muted small">
            La consulta firmada no se modifica. Cada corrección se registra como una enmienda que debe firmarse por separado.
          </p>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          {amendments.length > 0 && (
            <div className="mb-4">
              <AmendmentList
                amendments={amendments}
                renderActions={(a) => a.status === 'draft' && (
                  <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => startEdit(a)} disabled={busy}>Editar</button>
                    {canSignAmendment(a, user) && (
                      <button type="button" className="btn btn-success btn-sm" disabled={busy}
                        onClick={() => run(() => clinicalAmendmentService.sign(a.id, a.lock_version))}>
                        Firmar enmienda
                      </button>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          <h6>{editing ? 'Editar borrador de enmienda' : 'Nueva enmienda'}</h6>
          <div className="mb-2">
            <label className="form-label" htmlFor={`${titleId}-reason`}>Motivo de la enmienda *</label>
            <input id={`${titleId}-reason`} type="text" className="form-control" maxLength={500}
              value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor={`${titleId}-text`}>Texto de la enmienda *</label>
            <textarea id={`${titleId}-text`} className="form-control" rows={6}
              value={form.amendment_text} onChange={(e) => setForm((f) => ({ ...f, amendment_text: e.target.value }))} />
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => save(false)} disabled={busy}>Guardar borrador</button>
            {userCanSign && (
              <button type="button" className="btn btn-success btn-sm" onClick={() => save(true)} disabled={busy}>Guardar y firmar</button>
            )}
            {editing && (
              <button type="button" className="btn btn-light btn-sm" onClick={resetForm} disabled={busy}>
                Cancelar edición
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AmendmentDrawer;
